import { useState, useEffect, useRef, useCallback } from "react";
import TopicFrame from "../../../components/TopicFrame/TopicFrame";
import "./02_Histogrammspreizung.css";
import LoadedImage from "./../../../assets/images/lowcontrast.jpg";

// Custom hook for handling image processing
const useImageProcessing = (originalCanvasRef, contrastCanvasRef) => {
  const [imageData, setImageData] = useState(null);
  const [range, setRange] = useState({ min: 0, max: 255 });

  // Convert image to grayscale
  const convertToGrayscale = useCallback((ctx) => {
    const imgData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
      data[i] = data[i + 1] = data[i + 2] = avg;
    }
    ctx.putImageData(imgData, 0, 0);
    return imgData;
  }, []);

  // Update contrast based on range values
  const updateContrast = useCallback(() => {
    if (!imageData) return;
    
    const { min, max } = range;
    
    const contrastCtx = contrastCanvasRef.current?.getContext("2d");
    if (!contrastCtx) return;
    
    const newImageData = new ImageData(
      new Uint8ClampedArray(imageData.data),
      imageData.width,
      imageData.height
    );
    
    const data = newImageData.data;
    for (let i = 0; i < data.length; i += 4) {
      // Apply contrast stretching formula
      const value = ((data[i] - min) * 255) / (max - min);
      data[i] = data[i + 1] = data[i + 2] = Math.max(0, Math.min(255, value));
    }
    
    contrastCtx.putImageData(newImageData, 0, 0);
    
    return newImageData;
  }, [imageData, range]);

  // Load and initialize image
  const loadImage = useCallback(() => {
    const imageCanvas = originalCanvasRef.current;
    const contrastCanvas = contrastCanvasRef.current;
    
    if (!imageCanvas || !contrastCanvas) return;
    
    const imageCtx = imageCanvas.getContext("2d");
    
    const img = new Image();
    img.crossOrigin = "anonymous";
    
    img.onload = () => {
      // Set canvas dimensions
      imageCanvas.width = img.width;
      imageCanvas.height = img.height;
      contrastCanvas.width = img.width;
      contrastCanvas.height = img.height;
      
      // Draw image and convert to grayscale
      imageCtx.drawImage(img, 0, 0);
      const grayImageData = convertToGrayscale(imageCtx);
      setImageData(grayImageData);
    };
    
    img.src = LoadedImage;
  }, [originalCanvasRef, contrastCanvasRef, convertToGrayscale]);

  return {
    imageData,
    range,
    setRange,
    updateContrast,
    loadImage
  };
};

// Custom hook for handling histograms
const useHistogram = () => {
  // Draw histogram based on image data
  const drawHistogram = useCallback((histogramCanvas, imgData, sourceCanvas) => {
    if (!histogramCanvas || !imgData || !sourceCanvas) return;
    
    const histCtx = histogramCanvas.getContext("2d");
    histogramCanvas.width = sourceCanvas.width;
    histogramCanvas.height = 150;
    
    const pixels = imgData.data;
    const frequencies = new Array(256).fill(0);
    
    // Calculate frequency of each grayscale value
    for (let i = 0; i < pixels.length; i += 4) {
      const value = Math.round(pixels[i]);
      frequencies[value]++;
    }
    
    const maxFrequency = Math.max(...frequencies);
    const barWidth = histogramCanvas.width / 256;
    
    // Get accent color from CSS variables
    const accentColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--AccentColor")
      .trim() || "#3498db";
    
    histCtx.clearRect(0, 0, histogramCanvas.width, histogramCanvas.height);
    
    // Draw histogram bars
    for (let i = 0; i < 256; i++) {
      const height = (frequencies[i] / maxFrequency) * histogramCanvas.height;
      histCtx.fillStyle = accentColor;
      histCtx.fillRect(
        i * barWidth,
        histogramCanvas.height - height,
        barWidth,
        height
      );
    }
  }, []);

  // Draw grayscale gradient
  const drawGradient = useCallback((canvasWidth) => {
    const canvases = document.querySelectorAll(".gradientCanvas");
    canvases.forEach((canvas) => {
      canvas.width = canvasWidth;
      canvas.height = 10;
      canvas.style.border = "1px solid black";
      const ctx = canvas.getContext("2d");
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, "black");
      gradient.addColorStop(1, "white");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    });
  }, []);

  return {
    drawHistogram,
    drawGradient
  };
};

const HistogrammspreizungPage = () => {
  const originalCanvasRef = useRef(null);
  const contrastCanvasRef = useRef(null);
  const histogramCanvasRef = useRef(null);
  const histogramContrastCanvasRef = useRef(null);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(255);

  // Use custom hooks
  const { imageData, range, setRange, updateContrast, loadImage } = 
    useImageProcessing(originalCanvasRef, contrastCanvasRef);
  const { drawHistogram, drawGradient } = useHistogram();

  // Handle range slider changes
  const handleMinChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value >= maxValue) return; // Prevent min from exceeding max
    setMinValue(value);
    setRange(prev => ({ ...prev, min: value }));
  };

  const handleMaxChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value <= minValue) return; // Prevent max from being less than min
    setMaxValue(value);
    setRange(prev => ({ ...prev, max: value }));
  };

  // Initial setup
  useEffect(() => {
    loadImage();
  }, [loadImage]);

  // Update contrast and histograms when image data or range changes
  useEffect(() => {
    if (!imageData) return;
    
    const contrastImageData = updateContrast();
    
    // Draw original histogram
    drawHistogram(
      histogramCanvasRef.current,
      imageData,
      originalCanvasRef.current
    );
    
    // Draw contrast histogram
    if (contrastImageData) {
      drawHistogram(
        histogramContrastCanvasRef.current,
        contrastImageData,
        contrastCanvasRef.current
      );
    }
    
    // Draw gradients
    drawGradient(originalCanvasRef.current?.width);
    
  }, [imageData, range, updateContrast, drawHistogram, drawGradient]);

  return (
    <TopicFrame topicType="bildverarbeitung" entryNumber={1}>
      <div className="outerContainer">
        <div className="twoCanvas">
          <div className="canvasBorder">
            <h3>Originalbild</h3>
            <canvas ref={originalCanvasRef} aria-label="Original grayscale image"></canvas>
          </div>
          <div className="canvasBorder">
            <h3>Kontrastbild</h3>
            <canvas ref={contrastCanvasRef} aria-label="Contrast enhanced image"></canvas>
          </div>
        </div>
      </div>
      <div className="outerContainer">
        <div className="twoCanvas">
          <div className="canvasBorder">
            <h3>Original-Histogramm</h3>
            <canvas ref={histogramCanvasRef} aria-label="Histogram of original image"></canvas>
            <canvas className="gradientCanvas" aria-label="Grayscale gradient"></canvas>
          </div>
          <div className="canvasBorder">
            <h3>Kontrast-Histogramm</h3>
            <canvas ref={histogramContrastCanvasRef} aria-label="Histogram of contrast enhanced image"></canvas>
            <canvas className="gradientCanvas" aria-label="Grayscale gradient"></canvas>
          </div>
        </div>
      </div>

      <div className="slider-container">
        <div className="slider-group">
          <label htmlFor="minSlider">Minimaler Grauwert:</label>
          <input 
            type="range" 
            id="minSlider"
            className="slider" 
            min="0"
            max="254" 
            value={minValue}
            onChange={handleMinChange}
            aria-valuemin="0"
            aria-valuemax="254"
            aria-valuenow={minValue}
            aria-label="Minimum grayscale value"
          />
          <span id="minValue">{minValue}</span>
        </div>
        <div className="slider-group">
          <label htmlFor="maxSlider">Maximaler Grauwert:</label>
          <input 
            type="range" 
            id="maxSlider"
            className="slider"  
            min="1"
            max="255" 
            value={maxValue}
            onChange={handleMaxChange}
            aria-valuemin="1"
            aria-valuemax="255"
            aria-valuenow={maxValue}
            aria-label="Maximum grayscale value"
          />
          <span id="maxValue">{maxValue}</span>
        </div>
      </div>
    </TopicFrame>
  );
};

export default HistogrammspreizungPage;